import { useEffect, useMemo, useState } from 'react'
import { Drawer, Table, Input, Empty, Button, Modal, Message } from '@arco-design/web-react'
import { IconSearch } from '@arco-design/web-react/icon'
import { useTranslation } from 'react-i18next'
import {
  getUserTopupsApi,
  getAllTopupsApi,
  adminCompleteTopupApi,
  type TopUpRecord,
} from '@/api/topup'
import { Pill, renderTimestamp } from '@/utils/render'
import { renderQuota, displayAmountToQuota } from '@/utils/quota'
import { drawerWidth } from '@/utils/responsive'
import { getMethodLabel } from './PaymentMethodPicker'

const STATUS_VARIANT: Record<string, 'success' | 'warning' | 'danger' | 'neutral'> = {
  success: 'success',
  pending: 'warning',
  failed: 'danger',
  expired: 'danger',
}

interface TopupHistoryDrawerProps {
  visible: boolean
  onClose: () => void
  admin?: boolean
}

export default function TopupHistoryDrawer({ visible, onClose, admin = false }: TopupHistoryDrawerProps) {
  const { t } = useTranslation()
  const [loading, setLoading] = useState(false)
  const [items, setItems] = useState<TopUpRecord[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [keyword, setKeyword] = useState('')

  const statusText = useMemo(
    () => ({
      success: t('tt.topup.statusSuccess'),
      pending: t('tt.topup.statusPending'),
      failed: t('tt.topup.statusFailed'),
      expired: t('tt.topup.statusExpired'),
    }),
    [t]
  )

  const load = async (p = page, ps = pageSize, kw = keyword) => {
    setLoading(true)
    try {
      const fetcher = admin ? getAllTopupsApi : getUserTopupsApi
      const data = await fetcher({ p, page_size: ps, keyword: kw || undefined })
      setItems(data.items)
      setTotal(data.total)
    } catch (err: unknown) {
      Message.error(err instanceof Error ? err.message : t('tt.topup.loadFailed'))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (visible) {
      load(page, pageSize, keyword)
    }
  }, [visible, page, pageSize])

  const handleSearch = () => {
    setPage(1)
    load(1, pageSize, keyword)
  }

  const handleComplete = (tradeNo: string) => {
    Modal.confirm({
      title: t('tt.topup.confirmCompleteTitle'),
      content: t('tt.topup.completeOrderConfirm'),
      onOk: async () => {
        await adminCompleteTopupApi(tradeNo)
        Message.success(t('tt.topup.completeSuccess'))
        load()
      },
    })
  }

  const columns = [
    {
      title: t('tt.topup.orderNo'),
      dataIndex: 'trade_no',
      width: 200,
      render: (v: string) => <span className="font-mono text-xs">{v}</span>,
    },
    ...(admin
      ? [{ title: t('tt.topup.userId'), dataIndex: 'user_id', width: 90 }]
      : []),
    {
      title: t('tt.topup.paymentMethod'),
      dataIndex: 'payment_method',
      width: 100,
      render: (v: string) => getMethodLabel(v, t) || v || '-',
    },
    {
      title: t('tt.topup.topupQuota'),
      dataIndex: 'amount',
      width: 120,
      render: (v: number) => renderQuota(displayAmountToQuota(v)),
    },
    {
      title: t('tt.topup.paidShort'),
      dataIndex: 'money',
      width: 100,
      render: (v: number) => `¥${Number(v || 0).toFixed(2)}`,
    },
    {
      title: t('tt.topup.status'),
      dataIndex: 'status',
      width: 90,
      render: (v: string) => (
        <Pill variant={STATUS_VARIANT[v] || 'neutral'}>{statusText[v as keyof typeof statusText] || v}</Pill>
      ),
    },
    {
      title: t('tt.topup.createdAt'),
      dataIndex: 'create_time',
      width: 160,
      render: (v: number) => renderTimestamp(v),
    },
    ...(admin
      ? [
          {
            title: t('tt.topup.actions'),
            width: 100,
            render: (_: unknown, record: TopUpRecord) =>
              record.status === 'pending' ? (
                <Button size="mini" type="outline" onClick={() => handleComplete(record.trade_no)}>
                  {t('tt.topup.completeOrder')}
                </Button>
              ) : null,
          },
        ]
      : []),
  ]

  return (
    <Drawer
      title={t('tt.topup.history')}
      visible={visible}
      onCancel={onClose}
      width={drawerWidth(720)}
      footer={null}
    >
      <div className="mb-3 flex gap-2">
        <Input
          prefix={<IconSearch />}
          placeholder={t('tt.topup.searchOrder')}
          value={keyword}
          onChange={setKeyword}
          onPressEnter={handleSearch}
          allowClear
          className="flex-1"
        />
        <Button type="primary" onClick={handleSearch}>
          {t('tt.topup.search')}
        </Button>
      </div>
      <Table
        rowKey="id"
        loading={loading}
        columns={columns}
        data={items}
        scroll={{ x: 800 }}
        pagination={{
          current: page,
          pageSize,
          total,
          showTotal: true,
          sizeCanChange: true,
          onChange: (p, ps) => {
            setPage(p)
            setPageSize(ps)
          },
        }}
        noDataElement={<Empty description={t('tt.topup.historyEmpty')} />}
      />
    </Drawer>
  )
}
